import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc,
  serverTimestamp, query, orderBy,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { ReviewTemplate } from '../types';
import { DEFAULT_OPTICS_CRITERIA, DEFAULT_CHARACTER_CRITERIA } from '../types';

const COL = 'reviewTemplates';

export async function fetchTemplates(): Promise<ReviewTemplate[]> {
  const snap = await getDocs(query(collection(db, COL), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewTemplate);
}

export async function fetchTemplate(id: string): Promise<ReviewTemplate | null> {
  const snap = await getDoc(doc(db, COL, id));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as ReviewTemplate) : null;
}

export type TemplateInput = Omit<ReviewTemplate, 'id' | 'createdAt' | 'updatedAt'>;

export async function createTemplate(data: TemplateInput): Promise<string> {
  const ref = await addDoc(collection(db, COL), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function updateTemplate(id: string, data: Partial<TemplateInput>): Promise<void> {
  await updateDoc(doc(db, COL, id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteTemplate(id: string): Promise<void> {
  await deleteDoc(doc(db, COL, id));
}

/** Return a blank template with sensible defaults */
export function blankTemplate(createdBy: string): TemplateInput {
  return {
    name: '',
    scoringScale: 5,
    weightings: { okrIndividual: 0.40, optics: 0.30, character: 0.30 },
    teamVsIndividualSplit: { individual: 0.60, team: 0.40 },
    opticsCriteria: DEFAULT_OPTICS_CRITERIA,
    characterCriteria: DEFAULT_CHARACTER_CRITERIA,
    maxDiscretionDelta: 0.5,
    createdBy,
  };
}
